/**
 * The seven address components, as one component (0040; docs/02 "The address
 * standard").
 *
 * Every address in PropOS is typed into THIS grid — properties, vendors, tenant
 * organizations, all three contact kinds. One component is what makes that
 * claim true: separate copies is how one screen labels it "Zip", another
 * "Postal code", and a third puts State before City.
 *
 * The street is three fields because the number alone is something people
 * search by (ADR-33 derives a property alias from it) and because "4064 Colony
 * Road" typed into one box cannot be split back out afterwards without
 * guessing. The suffix is FREE TEXT with a datalist of the common dozen: USPS
 * publishes ~200 standard suffixes, so a picklist either refuses a real address
 * or is worse to use than typing three letters.
 */

import { COMMON_STREET_SUFFIXES, type AddressFormValues } from '@propos/shared';
import { useId, type ReactNode } from 'react';

import { TextField } from './fields.js';

/**
 * The postal components plus the attention line — what this component renders.
 * Purpose, the primary flag and notes are the CARD's controls, not the address
 * block's: the property create form embeds this block and has neither.
 */
export type AddressField = keyof Omit<AddressFormValues, 'purposeId' | 'isPrimary' | 'notes'>;

export function AddressFields({
  values,
  onChange,
  legend,
  showAttention = true,
}: {
  readonly values: AddressFormValues;
  readonly onChange: (field: AddressField, value: string) => void;
  /** Names whose address it is, when the surrounding section does not. */
  readonly legend?: string;
  /**
   * The attention line. On by default because a notice address without one is
   * usually incomplete; off where a record has no such convention (a property's
   * physical location is not addressed to anybody).
   */
  readonly showAttention?: boolean;
}): ReactNode {
  const suffixListId = useId();
  return (
    <div className="flex flex-col gap-4">
      {legend === undefined ? null : <p className="text-meta font-medium text-ink2">{legend}</p>}
      <div className="grid gap-4 sm:grid-cols-[1fr_3fr_1fr]">
        <TextField
          label="Street number"
          value={values.streetNumber}
          onChange={(event) => {
            onChange('streetNumber', event.target.value);
          }}
          maxLength={30}
          autoComplete="off"
        />
        <TextField
          label="Street name"
          value={values.streetName}
          onChange={(event) => {
            onChange('streetName', event.target.value);
          }}
          maxLength={200}
          autoComplete="off"
        />
        <TextField
          label="Suffix"
          value={values.streetSuffix}
          onChange={(event) => {
            onChange('streetSuffix', event.target.value);
          }}
          maxLength={40}
          list={suffixListId}
          autoComplete="off"
          hint="Rd, Blvd, …"
        />
      </div>
      {/* Suggestions, not a constraint: anything types. */}
      <datalist id={suffixListId}>
        {COMMON_STREET_SUFFIXES.map((suffix) => (
          <option key={suffix} value={suffix} />
        ))}
      </datalist>
      <TextField
        label="Address line 2"
        value={values.addressLine2}
        onChange={(event) => {
          onChange('addressLine2', event.target.value);
        }}
        maxLength={200}
        hint="Suite, floor, PO box"
      />
      <div className="grid gap-4 sm:grid-cols-[2fr_1fr_1fr]">
        <TextField
          label="City"
          value={values.city}
          onChange={(event) => {
            onChange('city', event.target.value);
          }}
          maxLength={120}
        />
        <TextField
          label="State"
          value={values.state}
          onChange={(event) => {
            onChange('state', event.target.value);
          }}
          maxLength={40}
        />
        <TextField
          label="ZIP"
          value={values.postalCode}
          onChange={(event) => {
            onChange('postalCode', event.target.value);
          }}
          maxLength={20}
        />
      </div>
      {showAttention ? (
        <TextField
          label="Attention"
          value={values.attention}
          onChange={(event) => {
            onChange('attention', event.target.value);
          }}
          maxLength={200}
          hint="The role a notice names — “Attn: General Counsel”"
        />
      ) : null}
    </div>
  );
}
